/** Admin API client: bearer access token, single-flight refresh, uniform error type. */

const STORAGE_KEY = "dlv-admin-session";

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, unknown> = {},
    public requestId?: string,
  ) {
    super(message);
  }
}

let session: Session | null = load();
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(s: Session | null) => void>();

function load(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  return session;
}

export function setSession(next: Session | null): void {
  session = next;
  try {
    if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable: keep the session in memory only */
  }
  listeners.forEach((fn) => fn(next));
}

export function onSessionChange(fn: (s: Session | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  auth?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const params = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function refresh(): Promise<boolean> {
  if (!session?.refreshToken) return false;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch("/admin/v1/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: session?.refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        setSession({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

async function toError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; details?: Record<string, unknown>; request_id?: string } } = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON error */
  }
  const err = body.error ?? {};
  return new ApiError(res.status, err.code ?? `http_${res.status}`, err.message ?? res.statusText, err.details ?? {}, err.request_id);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, auth = true } = opts;
  const attempt = async () => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (auth && session) headers.Authorization = `Bearer ${session.accessToken}`;
    return fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };
  let res = await attempt();
  if (res.status === 401 && auth && session) {
    if (await refresh()) res = await attempt();
    else setSession(null);
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  const type = res.headers.get("content-type") ?? "";
  return (type.includes("application/json") ? res.json() : res.text()) as Promise<T>;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>(path, { method: "POST", body: body ?? {}, query }),
  put: <T>(path: string, body?: unknown, query?: Query) => request<T>(path, { method: "PUT", body: body ?? {}, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: body ?? {} }),
  del: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Authenticated file download (template exports): fetch as a blob and hand it to the browser. */
export async function download(path: string, query?: Query): Promise<string> {
  const res = await fetch(buildUrl(path, query), { headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {} });
  if (!res.ok) throw await toError(res);
  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "download";
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

/** multipart/form-data POST (file uploads); the browser sets the boundary header. */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const send = () => fetch(buildUrl(path), { method: "POST", body: form, headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {} });
  let res = await send();
  if (res.status === 401 && session && (await refresh())) res = await send();
  if (!res.ok) throw await toError(res);
  return (await res.json()) as T;
}

/** POST that streams Server-Sent Events (used by the playground). */
export async function streamEvents(
  path: string,
  body: unknown,
  onEvent: (type: string, data: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const send = () =>
    fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
  let res = await send();
  if (res.status === 401 && (await refresh())) res = await send();
  if (!res.ok || !res.body) throw await toError(res);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let index: number;
    while ((index = buffer.indexOf("\n\n")) >= 0) {
      const chunk = buffer.slice(0, index);
      buffer = buffer.slice(index + 2);
      let type = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event: ")) type = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      try {
        onEvent(type, data ? JSON.parse(data) : {});
      } catch {
        onEvent(type, { raw: data });
      }
    }
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const issues = (err.details?.issues as { path: string; message: string }[] | undefined) ?? [];
    const first = issues.slice(0, 3).map((i) => `${i.path}: ${i.message}`);
    return [err.message, ...first].join(" — ");
  }
  return err instanceof Error ? err.message : String(err);
}
